import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { toast } from '@/shared/model/toast'
import type { ClientEventsFilter, ClientEventsResponse } from '@/shared/api/types'

function eventsQuery(filter: ClientEventsFilter): string {
  const params = new URLSearchParams()
  if (filter.important) params.set('important', '1')
  if (filter.campaignId) params.set('campaignId', String(filter.campaignId))
  if (filter.label !== undefined) params.set('label', filter.label)
  if (filter.limit) params.set('limit', String(filter.limit))
  if (filter.beforeId) params.set('beforeId', String(filter.beforeId))
  return params.toString()
}

export const clientEventsApi = {
  list: (filter: ClientEventsFilter, signal?: AbortSignal) => {
    const query = eventsQuery(filter)
    return api.get<ClientEventsResponse>(`/client-events${query ? `?${query}` : ''}`, signal)
  },
  markRead: (upToId: number) =>
    api.post<{ unread: number }>('/client-events/read', { upToId }),
}

export function useClientEvents(filter: ClientEventsFilter, refetchInterval?: number) {
  const query = eventsQuery(filter)
  return useQuery({
    queryKey: [...queryKeys.clientEvents, query],
    queryFn: ({ signal }) => clientEventsApi.list(filter, signal),
    refetchInterval,
  })
}

export function useMarkClientEventsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (upToId: number) => clientEventsApi.markRead(upToId),
    onSuccess: ({ unread }) => {
      queryClient.setQueriesData<ClientEventsResponse>(
        { queryKey: queryKeys.clientEvents },
        (previous) => (previous ? { ...previous, unread } : previous),
      )
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось отметить события')
    },
  })
}
